import React, { useState } from 'react'
import { Link, Mail, MapPin, MoreVertical, Star, User } from 'react-feather'
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  useIntl,
} from 'react-intl'
import { toast } from 'react-toastify'
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledButtonDropdown,
} from 'reactstrap'
import styled from 'styled-components'
import { queries } from '~/constants/mediaQuery'

const FILL = '#FFA84C'
const EMPTY = '#6D7278'
const WHITE = '#fff'
const REVIEW_TRANSLATION_REGEX = /\(Translated by Google\) (.|\n)*\n\n\(Original\)\n|\n\n\(Translated by Google\)\n(.|\n)*/
// const RATING_TO_CONST = ['ZERO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE']

const NPSReviewCard = ({ review }) => {
  const intl = useIntl()
  function handleCopyToClipboard(event) {
    event.preventDefault()
    const url = new URL(
      `/reviews/${encodeURIComponent(review?.id)}`,
      window.location.href
    )
    navigator.clipboard.writeText(url.toString())
    toast.success(intl.formatMessage({ id: 'Review link copied' }))
  }
  const wantContact = Boolean(review?.reviewerEmail)
  const [addressShow, setAddressShow] = useState(false)

  return (
    <CustomCard>
      <div className="first-row">
        <div className="contact-badge mobile" data-contact={wantContact}>
          <span>
            <FormattedMessage
              id={wantContact ? 'Want contact' : 'Anonymous user'}
            />
          </span>
        </div>
        <div className="rating-and-badge__wrapper">
          <div className="contact-badge desktop" data-contact={wantContact}>
            <span>
              <FormattedMessage
                id={wantContact ? 'Want contact' : 'Anonymous user'}
              />
            </span>
          </div>
          <div className="star-rating">
            <Star
              fill={review?.starRating >= 1 ? FILL : WHITE}
              stroke={review?.starRating >= 1 ? FILL : EMPTY}
              size={17}
            />
            <Star
              fill={review?.starRating >= 2 ? FILL : WHITE}
              stroke={review?.starRating >= 2 ? FILL : EMPTY}
              size={17}
            />
            <Star
              fill={review?.starRating >= 3 ? FILL : WHITE}
              stroke={review?.starRating >= 3 ? FILL : EMPTY}
              size={17}
            />
            <Star
              fill={review?.starRating >= 4 ? FILL : WHITE}
              stroke={review?.starRating >= 4 ? FILL : EMPTY}
              size={17}
            />
            <Star
              fill={review?.starRating >= 5 ? FILL : WHITE}
              stroke={review?.starRating >= 5 ? FILL : EMPTY}
              size={17}
            />
          </div>

          <div className="row-menu">
            <div>
              <FormattedDate format="dd/mm/yyyy" value={review?.createdAt} /> (
              <FormattedTime format="hh:mm" value={review?.createdAt} />)
            </div>
            <StyledDropdown>
              <DropdownToggle caret data-type="kebab">
                <MoreVertical size="15" color="#2F2B3DB2" />
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={handleCopyToClipboard}>
                  <div>
                    <Link size="15" color="#C4C4C4" />
                  </div>
                  <FormattedMessage id="Copy review link" />
                </DropdownItem>
              </DropdownMenu>
            </StyledDropdown>
          </div>
        </div>
      </div>
      <div className="location-row">
        <MapPin size={15} color="#2F2B3DB2" min={15} />
        <div
          data-show={addressShow}
          onClick={() => setAddressShow(prevState => !prevState)}
        >
          {review?.location?.address}
        </div>
      </div>
      {(Boolean(review.reviewerEmail) || Boolean(review.comment)) && (
        <div className="content-row">
          {Boolean(review.comment) && (
            <div className="content-row__wrapper">
              <div>
                <div>
                  <span>
                    {' '}
                    <FormattedDate
                      format="dd/mm/yyyy"
                      value={review?.createdAt}
                    />{' '}
                    (
                    <FormattedTime
                      format="hh:mm"
                      value={review?.createdAt}
                      timeZone="America/Sao_Paulo"
                    />
                    )
                  </span>
                </div>
                <p>{review.comment.replace(REVIEW_TRANSLATION_REGEX, '')}</p>
              </div>
            </div>
          )}

          {Boolean(review.reviewerEmail) && (
            <div className="content-row-contact__wrapper">
              {Boolean(review.reviewerName) && (
                <div>
                  <User className="icon" size={18} color="#2F2B3D" />
                  <span>{review?.reviewerName}</span>
                </div>
              )}
              <div>
                <Mail size={18} color="#2F2B3D" />
                <span>{review?.reviewerEmail}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </CustomCard>
  )
}

const CustomCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;

  .contact-badge {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    padding: 6px 14px;

    & > span {
      font-size: 13px;
      font-weight: 500;
      line-height: 18px; /* 138.462% */
      text-transform: capitalize;
      white-space: nowrap;
    }

    &.contact-badge[data-contact='true'] {
      border: 1px solid #ff4c51;
      background: var(--colorBorder, rgba(255, 76, 81, 0.16));
      color: #ff4c51;
    }
    &.contact-badge[data-contact='false'] {
      border: 1px solid var(--colorBorder, rgba(0, 0, 0, 0.15));
      background: rgba(128, 131, 144, 0.16);
      color: rgba(47 43 61, 0.9);
    }

    &.mobile {
      @media ${queries.more.sm} {
        display: none;
      }
    }

    &.desktop {
      @media ${queries.less.sm} {
        display: none;
      }
    }
  }

  > div {
    padding: 1rem;
  }

  .first-row {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);

    @media ${queries.less.sm} {
      align-items: start;
      flex-direction: column;
    }

    .star-rating {
      display: flex;
      justify-content: start;
      align-items: center;
      gap: 0.2rem;
    }

    .row-menu {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 1rem;
      margin-left: auto;

      @media ${queries.less.sm} {
        gap: 0.5rem;
      }

      > div:nth-child(1) {
        color: #2f2b3db2;
        font-size: 0.8rem;
      }
    }

    .rating-and-badge__wrapper {
      display: flex;
      align-items: center;
      gap: 16px;
      self-align: stretch;
      width: 100%;
      @media ${queries.less.sm} {
        justify-content: space-between;
      }
    }
  }

  .location-row {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: start;
    align-items: center;
    gap: 0.5rem;
    color: #979797;

    > div {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      overflow: hidden;

      &[data-show='true'] {
        -webkit-line-clamp: unset;
        overflow: unset;
      }
    }

    svg {
      min-width: 15px;
    }
  }

  .content-row {
    display: flex;
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    align-self: stretch;
    border-top: 1px solid var(--colorBorder, rgba(0, 0, 0, 0.15));

    .content-row__wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
      align-self: stretch;

      & > div {
        display: flex;
        padding: 12px;
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        align-self: stretch;
        border-radius: 0px 8px 8px 8px;
        background: #f8f7fa;

        & > div {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;

          span {
            color: #2f2b3d;
            opacity: 0.7;
            font-size: 10px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
          }
        }

        p {
          margin: 0;
          align-self: stretch;
          color: #2f2b3d;
          opacity: 0.9;
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: 21px; /* 150% */
        }
      }
    }

    .content-row-contact__wrapper {
      display: flex;
      max-width: 335px;
      overflow: hidden;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;

      > div {
        display: flex;
        align-items: center;
        gap: 6px;
        align-self: stretch;

        .icon {
          width: 18px;
          height: 18px;
        }

        & > span {
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
          overflow: hidden;
          color: text-primary;
          font-feature-settings: 'liga' off, 'clig' off;
          text-overflow: ellipsis;

          /* Basic Typography/body-2 */
          font-size: 13px;
          font-style: normal;
          font-weight: 400;
          line-height: 20px; /* 153.846% */
        }
      }
    }
  }
`

const StyledDropdown = styled(UncontrolledButtonDropdown)`
  .dropdown-toggle {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    background-color: #f7f7f7 !important;
    border: none !important;
    padding: 0.5rem !important;

    svg {
      position: auto !important;
      left: 0 !important;
    }

    &[data-type='kebab'] {
      background-color: #f7f7f7 !important;
    }
  }

  .dropdown-item {
    justify-content: start !important;
    gap: 0.5rem;

    > div {
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #f7f7f7;
      border-radius: 0.25rem;
      padding: 0.5rem;
    }
  }
`

export default NPSReviewCard
